"use client";

import React, {
  useRef,
  useEffect,
  forwardRef,
  useImperativeHandle,
} from "react";
import { useFrame, useThree } from "@react-three/fiber";
import * as THREE from "three";
import { INITIAL_CAMERA_POSITION } from "../../constants";

const EARTH_RADIUS = 6.371;

interface CameraAnimatorProps {
  followingSatelliteIndex: number | null;
  isAnimating: React.MutableRefObject<boolean>;
  getSatelliteMesh: (_index: number) => THREE.Mesh | null;
  onAnimationComplete?: (_type: "focus" | "unfocus") => void;
  earthXOffset: number;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  controlsRef: React.MutableRefObject<any>;
}

export interface CameraAnimatorRef {
  animateCameraToSatellite: (_satelliteIndex: number) => void;
  resetCamera: () => void;
  /** Estado mutable de la cámara en modo seguimiento (distance, theta, phi) */
  getFollowCameraState: () => { distance: number; theta: number; phi: number };
}

export const CameraAnimator = forwardRef<
  CameraAnimatorRef,
  CameraAnimatorProps
>(
  (
    {
      followingSatelliteIndex,
      isAnimating,
      getSatelliteMesh,
      onAnimationComplete,
      earthXOffset,
      controlsRef,
    },
    ref
  ) => {
    const { camera, scene, gl } = useThree();

    // Estado de animación de cámara
    const cameraAnimationRef = useRef<{
      type: "focus" | "unfocus";
      startTime: number;
      duration: number;
      startPosition: THREE.Vector3;
      startTarget: THREE.Vector3;
      startUp: THREE.Vector3;
      satelliteIndex: number;
    } | null>(null);

    // Estado del modo seguimiento
    const followCameraState = useRef({
      distance: 8,
      theta: 0,
      phi: Math.PI / 6,
    });
    const followTangentRef = useRef<THREE.Vector3 | null>(null);
    const prevRadialDirRef = useRef<THREE.Vector3 | null>(null);

    // Estado de puntero/rueda para modo seguimiento
    const isDraggingRef = useRef(false);
    const lastPointerRef = useRef({ x: 0, y: 0 });

    // Exponer métodos imperativos
    useImperativeHandle(ref, () => ({
      animateCameraToSatellite: (satelliteIndex: number) => {
        const controls = controlsRef.current;
        const satelliteMesh = getSatelliteMesh(satelliteIndex);

        if (!controls || !satelliteMesh) return;

        // Deshabilitar OrbitControls durante la animación para evitar interferencias
        controls.enabled = false;
        isAnimating.current = true;

        // Guardar parámetros de animación — el bucle principal de renderizado hará la interpolación
        cameraAnimationRef.current = {
          type: "focus",
          startTime: Date.now(),
          duration: 1000,
          startPosition: camera.position.clone(),
          startTarget: controls.target.clone(),
          startUp: camera.up.clone(),
          satelliteIndex,
        };
      },
      getFollowCameraState: () => followCameraState.current,
      resetCamera: () => {
        const controls = controlsRef.current;

        if (!controls) return;

        // Deshabilitar OrbitControls durante la animación
        controls.enabled = false;

        // Reiniciar estado del modo seguimiento
        prevRadialDirRef.current = null;
        followTangentRef.current = null;
        followCameraState.current = { distance: 8, theta: 0, phi: Math.PI / 6 };
        isAnimating.current = true;

        // Guardar parámetros de animación — el bucle principal de renderizado hará la interpolación
        cameraAnimationRef.current = {
          type: "unfocus",
          startTime: Date.now(),
          duration: 1000,
          startPosition: camera.position.clone(),
          startTarget: controls.target.clone(),
          startUp: camera.up.clone(),
          satelliteIndex: -1,
        };
      },
    }));

    // Bucle principal de animación
    useFrame(() => {
      const anim = cameraAnimationRef.current;
      const controls = controlsRef.current;

      if (!controls) return;

      // --- Animación de cámara (enfocar/desenfocar) ---
      if (anim) {
        // Asegurar que las matrices del mundo estén actualizadas para getWorldPosition
        scene.updateMatrixWorld(true);

        const elapsed = Date.now() - anim.startTime;
        const progress = Math.min(elapsed / anim.duration, 1);
        const eased = 1 - Math.pow(1 - progress, 3); // easeOutCubic

        if (anim.type === "focus") {
          const satelliteMesh = getSatelliteMesh(anim.satelliteIndex);
          if (satelliteMesh) {
            const currentSatWorldPos = new THREE.Vector3();
            satelliteMesh.getWorldPosition(currentSatWorldPos);
            const earthCenter = new THREE.Vector3(earthXOffset, 0, 0);
            const currentRadialDir = currentSatWorldPos
              .clone()
              .sub(earthCenter)
              .normalize();
            const cameraDistance = 8;

            // Posicionar cámara con offset phi (coincidiendo con el valor por defecto del modo seguimiento)
            // para que cámara-a-objetivo nunca sea paralelo a camera.up
            const followPhi = Math.PI / 6;
            const worldUp =
              Math.abs(currentRadialDir.y) < 0.99
                ? new THREE.Vector3(0, 1, 0)
                : new THREE.Vector3(1, 0, 0);
            const tangentX = new THREE.Vector3()
              .crossVectors(worldUp, currentRadialDir)
              .normalize();
            const currentTargetPos = currentSatWorldPos
              .clone()
              .addScaledVector(tangentX, cameraDistance * Math.sin(followPhi))
              .addScaledVector(
                currentRadialDir,
                cameraDistance * Math.cos(followPhi)
              );

            camera.position.lerpVectors(
              anim.startPosition,
              currentTargetPos,
              eased
            );
            camera.up
              .lerpVectors(anim.startUp, currentRadialDir, eased)
              .normalize();

            const lookTarget = new THREE.Vector3().lerpVectors(
              anim.startTarget,
              currentSatWorldPos,
              eased
            );
            camera.lookAt(lookTarget);
          }
        } else {
          // Animación de desenfoque
          const targetPosition = new THREE.Vector3(...INITIAL_CAMERA_POSITION);
          const targetTarget = new THREE.Vector3(earthXOffset, 0, 0);
          const targetUp = new THREE.Vector3(0, 1, 0);

          camera.position.lerpVectors(
            anim.startPosition,
            targetPosition,
            eased
          );
          camera.up.lerpVectors(anim.startUp, targetUp, eased).normalize();

          const lookTarget = new THREE.Vector3().lerpVectors(
            anim.startTarget,
            targetTarget,
            eased
          );
          camera.lookAt(lookTarget);
        }

        // Animación completada
        if (progress >= 1) {
          cameraAnimationRef.current = null;

          if (anim.type === "focus") {
            isAnimating.current = false;

            // Derivar estado de seguimiento desde la posición final real de la cámara
            const satelliteMesh = getSatelliteMesh(anim.satelliteIndex);
            if (satelliteMesh) {
              const finalSatWorldPos = new THREE.Vector3();
              satelliteMesh.getWorldPosition(finalSatWorldPos);
              const earthCenter = new THREE.Vector3(earthXOffset, 0, 0);
              const radialDir = finalSatWorldPos
                .clone()
                .sub(earthCenter)
                .normalize();
              const offset = camera.position.clone().sub(finalSatWorldPos);
              const distance = offset.length();

              const worldUp =
                Math.abs(radialDir.y) < 0.99
                  ? new THREE.Vector3(0, 1, 0)
                  : new THREE.Vector3(1, 0, 0);
              const tangentX = new THREE.Vector3()
                .crossVectors(worldUp, radialDir)
                .normalize();
              const tangentZ = new THREE.Vector3()
                .crossVectors(radialDir, tangentX)
                .normalize();

              const radialComponent = offset.dot(radialDir);
              const phi = Math.acos(
                Math.max(-1, Math.min(1, radialComponent / distance))
              );
              const theta = Math.atan2(
                offset.dot(tangentZ),
                offset.dot(tangentX)
              );

              followTangentRef.current = tangentX.clone();
              prevRadialDirRef.current = radialDir.clone();
              followCameraState.current = { distance, theta, phi };
              camera.up.copy(radialDir);
            }

            onAnimationComplete?.("focus");
          } else {
            // Desenfoque completado
            isAnimating.current = false;
            camera.up.set(0, 1, 0);
            controls.target.set(earthXOffset, 0, 0);
            controls.enabled = true;
            controls.enableDamping = true;
            controls.update();

            onAnimationComplete?.("unfocus");
          }
        }
      }

      // --- Modo seguimiento / controles por defecto ---
      if (followingSatelliteIndex !== null && !isAnimating.current && !anim) {
        const satelliteMesh = getSatelliteMesh(followingSatelliteIndex);
        if (satelliteMesh) {
          controls.enabled = false;

          // Forzar matrices del mundo para que getWorldPosition esté actualizado
          scene.updateMatrixWorld(true);

          const satWorldPos = new THREE.Vector3();
          satelliteMesh.getWorldPosition(satWorldPos);

          const earthCenter = new THREE.Vector3(earthXOffset, 0, 0);
          const radialDir = satWorldPos.clone().sub(earthCenter).normalize();

          // Construir marco local estable mediante transporte paralelo
          if (!followTangentRef.current) {
            const ref =
              Math.abs(radialDir.y) < 0.99
                ? new THREE.Vector3(0, 1, 0)
                : new THREE.Vector3(1, 0, 0);
            followTangentRef.current = new THREE.Vector3()
              .crossVectors(ref, radialDir)
              .normalize();
          } else {
            const t = followTangentRef.current;
            t.sub(
              radialDir.clone().multiplyScalar(t.dot(radialDir))
            ).normalize();
          }

          const tangentX = followTangentRef.current;
          const tangentZ = new THREE.Vector3()
            .crossVectors(radialDir, tangentX)
            .normalize();

          const { distance, theta, phi } = followCameraState.current;

          // Offset esférico en el marco radial local
          const offset = new THREE.Vector3()
            .addScaledVector(
              tangentX,
              distance * Math.sin(phi) * Math.cos(theta)
            )
            .addScaledVector(
              tangentZ,
              distance * Math.sin(phi) * Math.sin(theta)
            )
            .addScaledVector(radialDir, distance * Math.cos(phi));

          camera.position.copy(satWorldPos).add(offset);
          camera.up.copy(radialDir);
          camera.lookAt(satWorldPos);

          // Limitar para mantenerse por encima de la superficie terrestre
          const fromCenter = camera.position.clone().sub(earthCenter);
          const dist = fromCenter.length();
          const minDist = EARTH_RADIUS + 0.5;
          if (dist < minDist) {
            camera.position.copy(
              earthCenter
                .clone()
                .add(fromCenter.normalize().multiplyScalar(minDist))
            );
            camera.lookAt(satWorldPos);
          }
        }
      } else if (!isAnimating.current && !anim) {
        controls.enabled = true;
        controls.enableDamping = true;
        // Escalar sensibilidad de rotación con la distancia de la cámara
        const dist = camera.position.distanceTo(controls.target);
        controls.rotateSpeed = Math.pow(dist / 50, 2);
        controls.update();
      }
    });

    // Manejadores de eventos de puntero/rueda para modo seguimiento
    useEffect(() => {
      const container = gl.domElement.parentElement;
      if (!container) return;

      const handleFollowWheel = (e: WheelEvent) => {
        if (followingSatelliteIndex === null) return;
        e.preventDefault();
        const state = followCameraState.current;
        state.distance *= 1 + e.deltaY * 0.03;
        state.distance = Math.max(2, Math.min(50, state.distance));
      };

      const handleFollowPointerDown = (e: PointerEvent) => {
        if (followingSatelliteIndex === null) return;
        isDraggingRef.current = true;
        lastPointerRef.current = { x: e.clientX, y: e.clientY };
      };

      const handleFollowPointerMove = (e: PointerEvent) => {
        if (!isDraggingRef.current || followingSatelliteIndex === null) return;
        const dx = e.clientX - lastPointerRef.current.x;
        const dy = e.clientY - lastPointerRef.current.y;
        lastPointerRef.current = { x: e.clientX, y: e.clientY };
        const state = followCameraState.current;
        const sensitivity = 0.03 * (state.distance / 50);
        state.theta -= dx * sensitivity;
        state.phi = Math.max(
          0.05,
          Math.min(Math.PI / 2 - 0.05, state.phi + dy * sensitivity)
        );
      };

      const handleFollowPointerUp = () => {
        isDraggingRef.current = false;
      };

      container.addEventListener("wheel", handleFollowWheel, {
        passive: false,
      });
      container.addEventListener("pointerdown", handleFollowPointerDown);
      window.addEventListener("pointermove", handleFollowPointerMove);
      window.addEventListener("pointerup", handleFollowPointerUp);

      return () => {
        container.removeEventListener("wheel", handleFollowWheel);
        container.removeEventListener("pointerdown", handleFollowPointerDown);
        window.removeEventListener("pointermove", handleFollowPointerMove);
        window.removeEventListener("pointerup", handleFollowPointerUp);
      };
    }, [followingSatelliteIndex, gl.domElement]);

    // Este es un componente sin renderizado visual - retorna null
    return null;
  }
);

CameraAnimator.displayName = "CameraAnimator";

export default CameraAnimator;
